/**
 * @fileoverview Input/output schemas for the prose-expander-v2 agent.
 *
 * Style-passive: the agent matches the existing style of the draft and does
 * not consume a style guide or writing sample. Story-aware: receives chapter
 * structural context, plot state, POV, and (selectively) story genre to make
 * informed expansion decisions.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  // The draft + the user's directive
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  // Character voice (Voice attribute)
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  // Chapter structural context (key events / pacing intent / POV / boundary)
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  // Continuity context
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  // Genre awareness (only storyContext.genre is referenced in the prompt;
  // the full object is included so the field is in scope for templating).
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
}).partial({
  nextChapterContext: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining the expansion approach, what was added, and how it serves the chapter beats and genre (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The fully expanded chapter draft, formatted as a single HTML string. Should be roughly double the original length.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
